/**
 * Phone number utilities for international phone number handling.
 *
 * Supported countries:
 * - United States (+1)
 * - Canada (+1)
 * - Australia (+61)
 * - New Zealand (+64)
 * - Singapore (+65)
 * - South Africa (+27)
 *
 * @module phone
 */

import {
    CountryCode,
    parsePhoneNumberFromString,
    PhoneNumber,
} from 'libphonenumber-js'
import { Country } from '../constants/countries'

/**
 * Supported countries for phone number processing.
 * Country codes follow ISO 3166-1 alpha-2 standard.
 */
export const SUPPORTED_COUNTRIES: CountryCode[] = ['US', 'CA', 'AU', 'NZ', 'SG', 'ZA']

/**
 * Maps country names to ISO 3166-1 alpha-2 country codes.
 */
const COUNTRY_NAME_TO_CODE: Record<string, CountryCode> = {
    'united states': 'US',
    'united states of america': 'US',
    usa: 'US',
    us: 'US',
    canada: 'CA',
    ca: 'CA',
    australia: 'AU',
    au: 'AU',
    'new zealand': 'NZ',
    newzealand: 'NZ',
    nz: 'NZ',
    singapore: 'SG',
    sg: 'SG',
    'south africa': 'ZA',
    southafrica: 'ZA',
    za: 'ZA',
}

/**
 * Maps ISO country codes to E.164 calling codes.
 */
const COUNTRY_CODE_TO_CALLING_CODE: Record<string, string> = {
    US: '+1',
    CA: '+1',
    AU: '+61',
    NZ: '+64',
    SG: '+65',
    ZA: '+27',
}

/**
 * Maps ISO country codes to Country type.
 */
const ISO_CODE_TO_COUNTRY: Record<string, Country> = {
    US: 'United States',
    CA: 'Canada',
    AU: 'Australia',
    NZ: 'New Zealand',
    SG: 'Singapore',
    ZA: 'South Africa',
}

/**
 * Converts a country name string to an ISO 3166-1 alpha-2 country code.
 * Returns "US" as the default if the country is not recognized.
 *
 * @param country - The country name (e.g., "Australia", "New Zealand", "South Africa")
 * @returns The ISO country code (e.g., "AU", "NZ", "ZA")
 *
 * @example
 * getISOCountryCode("Australia") // => "AU"
 * getISOCountryCode("new zealand") // => "NZ"
 * getISOCountryCode("South Africa") // => "ZA"
 * getISOCountryCode(null) // => "US"
 */
export function getISOCountryCode(
    country: string | null | undefined,
): CountryCode {
    if (!country) return 'US'

    const normalized = country.toLowerCase().replace(/\s+/g, ' ').trim()

    return COUNTRY_NAME_TO_CODE[normalized] || 'US'
}

/**
 * Returns the E.164 calling code for a country (e.g., "+1", "+61").
 * Supports: United States, Canada, Australia, New Zealand, South Africa.
 *
 * @param country - The country name or ISO code
 * @returns The E.164 calling code (e.g., "+1", "+61", "+64", "+27")
 *
 * @example
 * getCountryCallingCode("Australia") // => "+61"
 * getCountryCallingCode("New Zealand") // => "+64"
 * getCountryCallingCode("South Africa") // => "+27"
 * getCountryCallingCode("US") // => "+1"
 */
export function getCountryCallingCode(
    country: string | null | undefined,
): string {
    const isoCode = getISOCountryCode(country)
    return COUNTRY_CODE_TO_CALLING_CODE[isoCode] || '+1'
}

/**
 * True when digits are a 10-digit NANP national number (or 11-digit with
 * leading 1). US/CA area codes that share prefixes with foreign calling
 * codes (650→SG +65, 555 placeholders, etc.) must not be parsed as
 * Singapore/Australia/etc. just because they fail US `isValid()`.
 */
function isNanpShaped(phone: string): boolean {
    const digits = phone.replace(/[^\d]/g, '')
    const national =
        digits.length === 11 && digits.startsWith('1')
            ? digits.slice(1)
            : digits.length === 10
              ? digits
              : null

    return national !== null && /^[2-9]\d{2}[2-9]\d{6}$/.test(national)
}

/**
 * Parses a phone number string and returns a PhoneNumber object.
 * Attempts to parse with the given country hint first, then tries each
 * supported country if needed.
 *
 * @param phone - The phone number string to parse
 * @param countryHint - Optional country hint for parsing ambiguous numbers
 * @returns A PhoneNumber object if parsing succeeds, null otherwise
 */
export function parsePhone(
    phone: string | null | undefined,
    countryHint?: string | null,
): PhoneNumber | null {
    if (!phone) return null

    const cleaned = phone.trim()
    if (!cleaned) return null

    // If the number starts with +, parse it directly
    if (cleaned.startsWith('+')) {
        const parsed = parsePhoneNumberFromString(cleaned)
        if (parsed?.isValid()) return parsed
        if (parsed?.countryCallingCode === '1' && isNanpShaped(cleaned)) {
            return parsed
        }
    }

    // NANP-shaped numbers are US/Canada even when libphonenumber rejects
    // them as invalid (555 placeholders, reserved NPAs). Otherwise SG/AU
    // accept the same digit string as a valid foreign number.
    if (isNanpShaped(cleaned)) {
        return (
            parsePhoneNumberFromString(cleaned, 'US') ||
            parsePhoneNumberFromString(cleaned, 'CA')
        )
    }

    // Try with the country hint first
    if (countryHint) {
        const isoCode = getISOCountryCode(countryHint)
        const parsed = parsePhoneNumberFromString(cleaned, isoCode)
        if (parsed?.isValid()) return parsed
    }

    // Try each supported country
    for (const countryCode of SUPPORTED_COUNTRIES) {
        const parsed = parsePhoneNumberFromString(cleaned, countryCode)
        if (parsed?.isValid()) return parsed
    }

    // Last resort: try parsing without country code validation
    const lastAttempt = parsePhoneNumberFromString(cleaned)
    return lastAttempt?.isValid() ? lastAttempt : null
}

/**
 * Formats a phone number to E.164 format.
 * E.164 is the international standard format: +[country code][number]
 *
 * @param phone - The phone number string to format
 * @param countryHint - Optional country hint for parsing ambiguous numbers
 * @returns The phone number in E.164 format (e.g., "+14155551234"), or null if invalid
 *
 * @example
 * formatPhoneToE164("(415) 555-1234", "US") // => "+14155551234"
 * formatPhoneToE164("0412 345 678", "Australia") // => "+61412345678"
 * formatPhoneToE164("021 123 4567", "New Zealand") // => "+64211234567"
 * formatPhoneToE164("082 123 4567", "South Africa") // => "+27821234567"
 */
export function formatPhoneToE164(
    phone: string | null | undefined,
    countryHint?: string | null,
): string | null {
    const parsed = parsePhone(phone, countryHint)
    return parsed?.format('E.164') || null
}

/**
 * Formats a phone number for storage (E.164 without leading "+").
 *
 * @param phone - The phone number string to format
 * @param countryHint - Optional country hint for parsing ambiguous numbers
 * @returns The phone number without leading "+" (e.g., "14155551234"), or the cleaned number if parsing fails
 *
 * @example
 * formatPhoneForStorage("(415) 555-1234", "US") // => "14155551234"
 * formatPhoneForStorage("0412 345 678", "Australia") // => "61412345678"
 * formatPhoneForStorage("021 123 4567", "New Zealand") // => "64211234567"
 */
export function formatPhoneForStorage(
    phone: string | null | undefined,
    countryHint?: string | null,
): string | null | undefined {
    if (!phone) return phone

    const e164 = formatPhoneToE164(phone, countryHint)
    if (e164) {
        // Remove the leading "+" for storage
        return e164.replace(/^\+/, '')
    }

    // Fallback: return digits only if parsing failed
    const digitsOnly = phone.replace(/[^\d]/g, '')
    return digitsOnly || null
}

/**
 * True when two stored phone strings reach the same destination, even if
 * they are saved in different formats (e.g. a 10-digit record and an
 * 11-digit record for the same US number, left behind by the E.164
 * storage-format migration).
 *
 * Comparison is done on the parsed E.164 form, so formatting differences
 * (spaces, dashes, parentheses, leading 1, missing +) are ignored. When
 * either side can't be parsed, the numbers are treated as different (fail
 * closed) rather than merging two numbers that only look alike.
 *
 * @param phoneA - First phone number string
 * @param phoneB - Second phone number string
 * @param countryHint - Optional country hint for parsing ambiguous numbers
 *
 * @example
 * isSamePhoneNumber("5417600965", "15417600965", "US") // => true
 * isSamePhoneNumber("(541) 760-0965", "+15417600965") // => true
 * isSamePhoneNumber("5417600965", "5417600966", "US") // => false
 * isSamePhoneNumber("5417600965", null) // => false
 */
export function isSamePhoneNumber(
    phoneA: string | null | undefined,
    phoneB: string | null | undefined,
    countryHint?: string | null,
): boolean {
    if (!phoneA || !phoneB) return false
    if (phoneA === phoneB) return true

    const e164A = formatPhoneToE164(phoneA, countryHint)
    const e164B = formatPhoneToE164(phoneB, countryHint)
    if (!e164A || !e164B) return false

    return e164A === e164B
}

/**
 * Formats a US/Canada number from digits when libphonenumber rejects it
 * (e.g. 555 placeholder numbers). Always includes +1:
 * "14155551234" / "4155551234" => "+1 (415) 555-1234".
 */
function formatNanpFallback(phone: string): string | null {
    const digits = cleanPhoneDigits(phone)
    const national =
        digits.length === 11 && digits.startsWith('1')
            ? digits.slice(1)
            : digits.length === 10
              ? digits
              : null

    if (!national) return null

    return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
}

/**
 * Formats a phone number for human-readable display.
 * Uses the national format for the detected country. US/Canada numbers
 * always include the +1 country code: "+1 (415) 555-1234".
 *
 * @param phone - The phone number string to format
 * @param countryHint - Optional country hint for parsing
 * @returns A human-readable phone number string, or null if invalid
 *
 * @example
 * formatPhoneReadable("+14155551234") // => "+1 (415) 555-1234"
 * formatPhoneReadable("4155551234") // => "+1 (415) 555-1234"
 * formatPhoneReadable("+61412345678") // => "0412 345 678"
 * formatPhoneReadable("+64211234567") // => "021 123 4567"
 * formatPhoneReadable("+27821234567") // => "082 123 4567"
 */
export function formatPhoneReadable(
    phone: string | null | undefined,
    countryHint?: string | null,
): string | null {
    if (!phone) return null

    const parsed = parsePhone(phone, countryHint)
    if (parsed) {
        const national = parsed.formatNational()
        if (parsed.countryCallingCode === '1') {
            return `+1 ${national}`
        }
        return national
    }

    return formatNanpFallback(phone)
}

/**
 * Formats a phone number in international format with the country code.
 *
 * @param phone - The phone number string to format
 * @param countryHint - Optional country hint for parsing
 * @returns An international format phone number, or null if invalid
 *
 * @example
 * formatPhoneInternational("+14155551234") // => "+1 415 555 1234"
 * formatPhoneInternational("+61412345678") // => "+61 412 345 678"
 */
export function formatPhoneInternational(
    phone: string | null | undefined,
    countryHint?: string | null,
): string | null {
    const parsed = parsePhone(phone, countryHint)
    if (!parsed) return null

    return parsed.formatInternational()
}

/**
 * Validates a phone number for the specified country.
 *
 * @param phone - The phone number string to validate
 * @param countryHint - Optional country hint for validation
 * @returns true if the phone number is valid, false otherwise
 *
 * @example
 * isValidPhone("+14155551234") // => true
 * isValidPhone("(415) 555-1234", "US") // => true
 * isValidPhone("invalid") // => false
 */
export function isValidPhone(
    phone: string | null | undefined,
    countryHint?: string | null,
): boolean {
    if (!phone) return false

    const parsed = parsePhone(phone, countryHint)
    return parsed?.isValid() ?? false
}

/**
 * Determines the country of a phone number.
 *
 * @param phone - The phone number string
 * @param countryHint - Optional country hint for parsing
 * @returns The ISO country code (e.g., "US", "AU"), or null if undetermined
 *
 * @example
 * getPhoneCountry("+14155551234") // => "US"
 * getPhoneCountry("+61412345678") // => "AU"
 * getPhoneCountry("+64211234567") // => "NZ"
 * getPhoneCountry("+27821234567") // => "ZA"
 */
export function getPhoneCountry(
    phone: string | null | undefined,
    countryHint?: string | null,
): CountryCode | null {
    const parsed = parsePhone(phone, countryHint)
    return (parsed?.country as CountryCode) || null
}

/**
 * Gets the Country type name from a phone number.
 *
 * @param phone - The phone number string
 * @param countryHint - Optional country hint for parsing
 * @returns The country name or null if undetermined
 */
export function getPhoneCountryName(
    phone: string | null | undefined,
    countryHint?: string | null,
): Country | null {
    const isoCode = getPhoneCountry(phone, countryHint)
    if (!isoCode) return null
    return ISO_CODE_TO_COUNTRY[isoCode] || null
}

/**
 * Extracts just the digits from a phone number string.
 *
 * @param phone - The phone number string
 * @returns Only the digits from the phone number
 *
 * @example
 * cleanPhoneDigits("(415) 555-1234") // => "4155551234"
 * cleanPhoneDigits("+1 (415) 555-1234") // => "14155551234"
 */
export function cleanPhoneDigits(phone: string | null | undefined): string {
    if (!phone) return ''
    return phone.replace(/[^\d]/g, '')
}

/**
 * Checks if a phone number is a mobile number.
 * Note: This is not 100% reliable for all countries.
 *
 * @param phone - The phone number string
 * @param countryHint - Optional country hint for parsing
 * @returns true if likely a mobile number, false otherwise
 */
export function isMobilePhone(
    phone: string | null | undefined,
    countryHint?: string | null,
): boolean {
    const parsed = parsePhone(phone, countryHint)
    if (!parsed) return false

    const type = parsed.getType()
    return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE'
}

/**
 * Gets the national number portion without country code.
 *
 * @param phone - The phone number string
 * @param countryHint - Optional country hint for parsing
 * @returns The national number portion, or null if parsing fails
 *
 * @example
 * getNationalNumber("+14155551234") // => "4155551234"
 * getNationalNumber("+61412345678") // => "412345678"
 */
export function getNationalNumber(
    phone: string | null | undefined,
    countryHint?: string | null,
): string | null {
    const parsed = parsePhone(phone, countryHint)
    return parsed?.nationalNumber || null
}

/**
 * Creates a privacy-masked preview of a phone number.
 * Shows only the last few digits for verification purposes.
 *
 * @param phone - The phone number string
 * @param countryHint - Optional country hint for parsing
 * @returns A masked phone preview (e.g., "***-***-1234")
 */
export function getPhonePreview(
    phone: string | null | undefined,
    countryHint?: string | null,
): string {
    if (!phone) return ''

    const parsed = parsePhone(phone, countryHint)
    const country = parsed?.country

    switch (country) {
        case 'AU':
            // Australian format: 04**-***-XXX (show last 3)
            return phone ? `04**-***-${phone.slice(-3)}` : ''
        case 'NZ':
            // New Zealand format: 02*-***-XXXX (show last 4)
            return phone ? `02*-***-${phone.slice(-4)}` : ''
        case 'SG':
            // Singapore format: ****-XXXX (show last 4)
            return phone ? `****-${phone.slice(-4)}` : ''
        case 'ZA':
            // South Africa format: 0**-***-XXXX (show last 4)
            return phone ? `0**-***-${phone.slice(-4)}` : ''
        case 'US':
        case 'CA':
        default:
            // US/Canada/default format: ***-***-XXXX (show last 4)
            return phone ? `***-***-${phone.slice(-4)}` : ''
    }
}

/**
 * Validates and formats a phone number for Firebase Auth.
 * Returns the phone in E.164 format required by Firebase.
 *
 * @param phone - The phone number string
 * @param country - The country name
 * @returns The phone in E.164 format, or the original if parsing fails
 */
export function formatPhoneForFirebaseAuth(
    phone: string,
    country: string,
): string {
    const e164 = formatPhoneToE164(phone, country)
    if (e164) return e164

    // Fallback: manual formatting for common cases
    const cleaned = ('' + phone).replace(/\D/g, '')

    const countryLower = country.toLowerCase()

    if (countryLower === 'australia') {
        // Remove leading 0 if present and add +61
        const aussieCleaned = cleaned.replace(/^0/, '')
        if (aussieCleaned.startsWith('61')) {
            return '+' + aussieCleaned
        }
        if (aussieCleaned.length === 9) {
            return '+61' + aussieCleaned
        }
    }

    if (countryLower === 'new zealand') {
        // Remove leading 0 if present and add +64
        const nzCleaned = cleaned.replace(/^0/, '')
        if (nzCleaned.startsWith('64')) {
            return '+' + nzCleaned
        }
        if (nzCleaned.length >= 8 && nzCleaned.length <= 9) {
            return '+64' + nzCleaned
        }
    }

    if (countryLower === 'south africa') {
        // Remove leading 0 if present and add +27
        const zaCleaned = cleaned.replace(/^0/, '')
        if (zaCleaned.startsWith('27')) {
            return '+' + zaCleaned
        }
        if (zaCleaned.length === 9) {
            return '+27' + zaCleaned
        }
    }

    if (countryLower === 'singapore') {
        if (cleaned.startsWith('65') && cleaned.length === 10) {
            return '+' + cleaned
        }
        if (cleaned.length === 8) {
            return '+65' + cleaned
        }
    }

    if (countryLower === 'united states' || countryLower === 'canada') {
        let naCleaned = cleaned
        if (naCleaned.startsWith('1')) {
            naCleaned = naCleaned.slice(1)
        }
        if (naCleaned.length === 10) {
            return '+1' + naCleaned
        }
    }

    // Fallback: return as is
    return phone
}

/**
 * Validates a phone number for Firebase Auth.
 *
 * @param phone - The phone number string
 * @param country - The country name
 * @returns true if the phone is valid for Firebase Auth
 */
export function validatePhoneForFirebaseAuth(
    phone: string,
    country: string,
): boolean {
    const cleaned = ('' + phone).replace(/\D/g, '')
    const countryLower = country.toLowerCase()

    if (countryLower === 'australia') {
        const aussieCleaned = cleaned.replace(/^0/, '')

        // Australian mobile numbers: 9 digits starting with 4
        if (aussieCleaned.length === 9 && aussieCleaned.startsWith('4')) {
            return true
        }

        // Australian landline numbers: 8 digits
        if (aussieCleaned.length === 8) {
            return true
        }

        // International format with +61
        if (
            aussieCleaned.startsWith('61') &&
            (aussieCleaned.length === 11 || aussieCleaned.length === 10)
        ) {
            return true
        }

        return false
    }

    if (countryLower === 'new zealand') {
        const nzCleaned = cleaned.replace(/^0/, '')

        // New Zealand mobile numbers: 8-9 digits starting with 2
        if (
            (nzCleaned.length === 8 || nzCleaned.length === 9) &&
            nzCleaned.startsWith('2')
        ) {
            return true
        }

        // International format with +64
        if (
            nzCleaned.startsWith('64') &&
            (nzCleaned.length === 10 || nzCleaned.length === 11)
        ) {
            return true
        }

        return false
    }

    if (countryLower === 'south africa') {
        const zaCleaned = cleaned.replace(/^0/, '')

        // South African mobile numbers: 9 digits starting with 6, 7, or 8
        if (zaCleaned.length === 9 && ['6', '7', '8'].includes(zaCleaned[0])) {
            return true
        }

        // International format with +27
        if (zaCleaned.startsWith('27') && zaCleaned.length === 11) {
            return true
        }

        return false
    }

    if (countryLower === 'singapore') {
        // Singaporean numbers: 8 digits starting with 6, 8, or 9
        if (cleaned.startsWith('65') && cleaned.length === 10) {
            return ['6', '8', '9'].includes(cleaned[2])
        }
        return cleaned.length === 8 && ['6', '8', '9'].includes(cleaned[0])
    }

    if (countryLower === 'united states' || countryLower === 'canada') {
        let naCleaned = cleaned
        if (naCleaned.startsWith('1')) {
            naCleaned = naCleaned.slice(1)
        }

        // North American numbers: 10 digits
        return naCleaned.length === 10
    }

    return false
}
